import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ContentCopyOutlined as CopyIcon,
  IosShareOutlined as ShareIcon,
  LinkOutlined as LinkIcon,
  OpenInNewOutlined as OpenInNewIcon,
} from '@mui/icons-material';
import { propertyBookingUrl, propertyLabel } from '../utils/showings';

const canNativeShare = () =>
  typeof navigator !== 'undefined' && typeof navigator.share === 'function';

/**
 * Landlord's public booking link with copy / open / share, plus one link per property.
 * Native share (text, email, WhatsApp) is offered where the browser supports it — mainly phones.
 */
const BookingLinkCard = ({ url, properties = [], propertyIdsWithAvailability = new Set(), onMessage, onError }) => {
  const nativeShare = canNativeShare();

  const copyLink = async (link, label) => {
    try {
      await navigator.clipboard.writeText(link);
      onMessage(label ? `Link for ${label} copied.` : 'Booking link copied.');
    } catch (err) {
      console.error('❌ Clipboard copy failed:', err);
      onError('Could not copy automatically. Press and hold the link to copy it.');
    }
  };

  const shareLink = async (link, label) => {
    if (!nativeShare) {
      await copyLink(link, label);
      return;
    }
    try {
      await navigator.share({
        title: label ? `Book a showing — ${label}` : 'Book a showing',
        text: 'Pick a day and time that works for you:',
        url: link,
      });
    } catch (err) {
      if (err?.name === 'AbortError') return;
      console.error('❌ Share failed:', err);
      await copyLink(link, label);
    }
  };

  if (!url) return null;

  return (
    <Card sx={{ mb: { xs: 2, sm: 3 } }}>
      <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Typography
          variant="h6"
          component="h2"
          sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
        >
          <LinkIcon /> Your booking link
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
          One link for all your properties. Prospects pick a property, day, and time from your open
          availability, then you confirm.
        </Typography>

        <Box
          component="a"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: 'block',
            p: 1.25,
            mb: 1.5,
            borderRadius: 2,
            bgcolor: 'action.hover',
            color: 'text.primary',
            fontSize: '0.875rem',
            textDecoration: 'none',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          aria-label="Booking link"
        >
          {url}
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {nativeShare && (
            <Button
              variant="contained"
              startIcon={<ShareIcon />}
              onClick={() => shareLink(url)}
              sx={{ flex: { xs: '1 1 100%', sm: '0 0 auto' } }}
            >
              Share
            </Button>
          )}
          <Button
            variant={nativeShare ? 'outlined' : 'contained'}
            startIcon={<CopyIcon />}
            onClick={() => copyLink(url)}
            aria-label="Copy booking link"
            sx={{ flex: { xs: 1, sm: '0 0 auto' } }}
          >
            Copy
          </Button>
          <Button
            variant="outlined"
            startIcon={<OpenInNewIcon />}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ flex: { xs: 1, sm: '0 0 auto' } }}
          >
            Open
          </Button>
        </Box>

        {properties.length > 1 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Or share one property
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Opens with that property already picked.
            </Typography>
            <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
              {properties.map((property) => {
                const label = propertyLabel(property) || 'Property';
                const link = propertyBookingUrl(url, property.id);
                const hasTimes = propertyIdsWithAvailability.has(property.id);
                return (
                  <Box
                    component="li"
                    key={property.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      py: 1,
                      borderTop: '1px solid',
                      borderColor: 'divider',
                      '&:first-of-type': { borderTop: 'none' },
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, overflowWrap: 'anywhere' }}>
                        {label}
                      </Typography>
                      <Typography variant="caption" color={hasTimes ? 'success.main' : 'text.secondary'}>
                        {hasTimes ? 'Has open times' : 'No open times yet — add availability first'}
                      </Typography>
                    </Box>
                    <Tooltip title="Copy link">
                      <IconButton aria-label={`Copy link for ${label}`} onClick={() => copyLink(link, label)}>
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {nativeShare && (
                      <Tooltip title="Share link">
                        <IconButton
                          aria-label={`Share link for ${label}`}
                          onClick={() => shareLink(link, label)}
                        >
                          <ShareIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                );
              })}
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default BookingLinkCard;
